import { getAnswerBatched } from 'api/answerBatchFetch'
import queryClient from 'api/queryClient'
import { getCachedAnswer, isViewerHydratedAnswerCacheEntry } from 'state/cache'
import { threadOpenContextIds } from 'lib/threadOpenReveal'
import { prepareQuestForOpen } from 'refactor/panels/primeChildQuestForOpen'
import { Quest } from 'types/quests'

export type OpenRightPreparation = { quest: Quest | null; contextIds: string[] }
type Preparation = { result: Promise<OpenRightPreparation>; completion: Promise<void> }

const speculativeInFlight = new Map<string, Promise<OpenRightPreparation>>()
const preparationInFlight = new Map<string, Preparation>()
let speculativeActive = 0
const MAX_SPECULATIVE_DESTINATIONS = 2

const normalizedId = (id: string | number | null | undefined) =>
    id == null || id === '' ? null : String(id)

/**
 * Prepare the data needed to reveal a post in a newly opened right panel.
 *
 * The target body and the quest membership request intentionally start in the
 * same turn. Membership supplies the descriptors needed to mount the target;
 * the reveal waits for its actual DOM. Body requests never delay those descriptors.
 */
function getOrStartPreparation(questId: string, answerId: string): Preparation {
    const key = `${questId}:${answerId}`
    const existing = preparationInFlight.get(key)
    if (existing) return existing

    const targetPromise = isViewerHydratedAnswerCacheEntry(getCachedAnswer(answerId))
        ? Promise.resolve(getCachedAnswer(answerId))
        : queryClient
              .fetchQuery({
                  queryKey: ['answer', answerId],
                  queryFn: async () => {
                      const answer = await getAnswerBatched(answerId)
                      if (!answer)
                          throw Object.assign(new Error('Answer unavailable'), { status: 404 })
                      return answer
                  },
                  staleTime: 0,
                  networkMode: 'always',
              })
              .catch(() => null)
    const membershipPromise = prepareQuestForOpen(questId, answerId).catch(() => null)
    let neighborCompletion: Promise<void> = Promise.resolve()
    const result = membershipPromise.then(quest => {
        if (!quest || quest.__knovAnswerHydrationScope === 'stream-preview')
            return { quest: quest || null, contextIds: [answerId] }
        const membershipIds = [
            quest.parent?.id || quest.parent_id,
            ...(quest.sorted_answer_ids || quest.sorted_answers?.map(answer => answer.id) || []),
        ]
            .filter(id => id != null)
            .map(String)
        const contextIds = threadOpenContextIds(membershipIds, answerId)
        const neighborPromises = contextIds
            .filter(id => id !== answerId && !isViewerHydratedAnswerCacheEntry(getCachedAnswer(id)))
            .map(id => getAnswerBatched(id).catch(() => null))
        neighborCompletion = Promise.allSettled(neighborPromises).then(() => undefined)
        return { quest, contextIds }
    })
    const completion = Promise.allSettled([targetPromise, result]).then(async () => {
        await neighborCompletion
    })
    const preparation = { result, completion }
    preparationInFlight.set(key, preparation)
    // A failed membership/target attempt must be retryable even while an optional
    // neighbor is still loading. Entity requests already dedupe their own bodies.
    const forgetPreparation = () => {
        if (preparationInFlight.get(key) === preparation) preparationInFlight.delete(key)
    }
    void result.then(forgetPreparation, forgetPreparation)
    return preparation
}

export function prepareOpenRight(
    questId: string | number | null | undefined,
    answerId: string | number | null | undefined,
): Promise<OpenRightPreparation> {
    const normalizedQuestId = normalizedId(questId)
    const normalizedAnswerId = normalizedId(answerId)
    if (!normalizedQuestId || !normalizedAnswerId)
        return Promise.resolve({ quest: null, contextIds: [] })
    return getOrStartPreparation(normalizedQuestId, normalizedAnswerId).result
}

/**
 * Best-effort speculative preparation for hover/focus/touch affordances.
 * Clicks should call prepareOpenRight directly and therefore bypass this cap.
 */
export function prefetchOpenRight(questId: string | number, answerId: string | number) {
    const key = `${String(questId)}:${String(answerId)}`
    const existing = speculativeInFlight.get(key)
    if (existing) return existing
    if (speculativeActive >= MAX_SPECULATIVE_DESTINATIONS)
        return Promise.resolve<OpenRightPreparation>({ quest: null, contextIds: [] })

    speculativeActive += 1
    const preparation = getOrStartPreparation(String(questId), String(answerId))
    const promise = preparation.result
        .catch(() => ({ quest: null, contextIds: [] }))
        .then(async value => {
            await preparation.completion
            return value
        })
        .finally(() => {
            speculativeActive -= 1
            speculativeInFlight.delete(key)
        })
    speculativeInFlight.set(key, promise)
    return promise
}
